import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { isKnownPath } from "./AllValidPaths";
import NotAuthorized from "../pages/Auth/NotAuthorized";
// import SessionProvider from "../hooks/SessionProvider";
// import { TokenService } from "../services/Api";

// Auth Pages
import Login from "../pages/Auth/Login";
import NotFound from "../pages/Auth/NotFound";

// Layouts
import HomeLayout from "../layout/homeLayout/HomeLayout";
import DashboardLayout from "../layout/productAdminLayout/DashboardLayout";
import UserLayout from "../layout/userLayout/UserLayout";

// Home Layout Pages
// import HomeScreen from "../layout/homeLayout/HomeScreen";
import DemoRequestCreate from "../layout/homeLayout/DemoRequestCreate";

// Product Admin Dashboard Pages
import DashboardHome from "../pages/dashboard/DashboardHome";
import Profile from "../pages/profile/Profile";
import LicenseList from "../pages/License/LicenseList";
import LicenseCreate from "../pages/License/LicenseCreate";
import UserManagement from "../pages/masters/UserManagement";
import School from "../pages/masters/School";
import RequestDemo from "../pages/masters/RequestDemo";

// Sample Pages (can be removed in production)
import SampleInputUsage from "../pages/sample/SampleInputUsage";
import SampleButtonUsage from "../pages/sample/SampleButtonUsage";
import SampleTimePickerUsage from "../pages/sample/SampleTimePickerUsage";
import SampleMonthPickerUsage from "../pages/sample/SampleMonthPickerUsage";
import SampleDatePickerUsage from "../pages/sample/SampleDatePickerUsage";
import SampleDropdownUsage from "../pages/sample/SampleDropdownUsage";
import SampleForm from "../pages/sample/SampleForm";

// Route Protection Components
import { ProtectedRoute, PublicRoute } from "./ProtectedRoute";

// User Role Route Configurations
import { groupCockpitRoutes } from "./GroupCockpitRoutes";
import { staffRoutes } from "./StaffRoutes";
import { studentRoutes } from "./StudentRoutes";
import { schoolCockpitRoutes } from "./SchoolCockpitRoutes";
import { parentRoutes } from "./ParentRoutes";
import ChangePassword from "../pages/Auth/ChangePassword";
import ForgotPassword from "../pages/Auth/ForgotPassword";
import AdmissionApplicationFormX from "../pages/admissionApplicationForm/admissionApplicationFormX/AdmissionApplicationFormX";
import ApplicationXStatus from "../pages/admissionApplicationForm/admissionApplicationFormX/ApplicationXStatus";
import ApplicationXWithdrawal from "../pages/admissionApplicationForm/admissionApplicationFormX/ApplicationXWithdrawal";
import AdmissionApplicationFormXII from "../pages/admissionApplicationForm/admissionApplicationFormXII/AdmissionApplicationFormXII";
import ApplicationXIIStatus from "../pages/admissionApplicationForm/admissionApplicationFormXII/ApplicationXIIStatus";
import ApplicationXIIWithdrawal from "../pages/admissionApplicationForm/admissionApplicationFormXII/ApplicationXIIWithdrawal";
import WhyIFillip from "../pages/whyifillip/WhyIFillip";
import ExploreMore from "../pages/exploremore/ExploreMore";
import PrivacyPolicy from "../pages/privacypolicy/Privacypolicy";

const SmartCatchAll = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  const pathExists = isKnownPath(location.pathname);

  // CASE A: Not logged in
  if (!user) {
    if (pathExists) {
      // URL is a real protected route → send to login
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // URL doesn't exist anywhere → 404
    return <NotFound />;
  }

  // CASE B: Logged in
  if (pathExists) {
    // URL exists but not registered for this user's role → Not Authorized popup
    return <NotAuthorized />;
  }
  // URL doesn't exist anywhere → 404
  return <NotFound />;
};

// ==================== GET ROUTES BY ROLE ====================
/**
 * Get route configuration based on role ID
 * @param {number} roleId - User's role ID
 * @returns {Array} - Array of route configurations
 */
const getRoutesByRole = (roleId) => {
  // console.log("Resolved roleId:", roleId, typeof roleId);

  switch (roleId) {
    case 2:
      return groupCockpitRoutes;
    case 3:
      return staffRoutes;
    case 4:
      return studentRoutes;
    case 5:
      return schoolCockpitRoutes;
    case 6:
      return parentRoutes;
    // default:
    //   return groupCockpitRoutes;
    default:
      return [];
  }
};

// ==================== USER ROUTES RENDERER ====================
/**
 * Renders user routes based on role
 * Returns Route components from route configuration array
 */
const renderUserRoutes = (user) => {
  const roleId = Number(user?.role?.id);

  // console.group("renderUserRoutes DEBUG");
  // console.log("user:", user);
  // console.log("roleId:", roleId);
  // console.groupEnd();

  const routes = getRoutesByRole(roleId);

  return routes.map((route, index) => (
    <Route key={index} path={route.path} element={route.element} />
  ));
};

// ==================== MAIN APP ROUTES ====================
const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) return null;

  return (
    <Routes>
      {/* ==================== ROOT REDIRECT ==================== */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* ==================== PUBLIC ROUTES WITH HOME LAYOUT ==================== */}
      <Route element={<HomeLayout />}>
        {/* <Route path="/" element={<HomeLayout />}> */}
        {/* <Route index element={<HomeScreen />} /> */}
        <Route path="request-demo" element={<DemoRequestCreate />} />
        <Route path="why-ifillip" element={<WhyIFillip />} />
        <Route path="explore-more" element={<ExploreMore />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
      </Route>

      {/* ==================== AUTH ROUTES ==================== */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/change-password/:email"
        element={
          <PublicRoute allowWhenLoggedIn>
            <ChangePassword />
          </PublicRoute>
        }
      />
      {/* ==================== ADMISSION FORM X ROUTES ==================== */}
      <Route
        path="/admission-application-form-x/*"
        element={
          <PublicRoute allowWhenLoggedIn>
            <AdmissionApplicationFormX />
          </PublicRoute>
        }
      />
      <Route
        path="/application-x-status/:id"
        element={
          <PublicRoute allowWhenLoggedIn>
            <ApplicationXStatus />
          </PublicRoute>
        }
      />
      <Route
        path="/application-x-withdraw/:id"
        element={
          <PublicRoute allowWhenLoggedIn>
            <ApplicationXWithdrawal />
          </PublicRoute>
        }
      />
      <Route
        path="/admission-application-form-xii/*"
        element={
          <PublicRoute allowWhenLoggedIn>
            <AdmissionApplicationFormXII />
          </PublicRoute>
        }
      />
      <Route
        path="/application-xii-status/:id"
        element={
          <PublicRoute allowWhenLoggedIn>
            <ApplicationXIIStatus />
          </PublicRoute>
        }
      />
      <Route
        path="/application-xii-withdraw/:id"
        element={
          <PublicRoute allowWhenLoggedIn>
            <ApplicationXIIWithdrawal />
          </PublicRoute>
        }
      />

      {/* Sample Pages */}
      <Route path="form" element={<SampleForm />} />
      <Route path="sample-input" element={<SampleInputUsage />} />
      <Route path="sample-button" element={<SampleButtonUsage />} />
      <Route path="sample-timepicker" element={<SampleTimePickerUsage />} />
      <Route path="sample-monthpicker" element={<SampleMonthPickerUsage />} />
      <Route path="sample-datepicker" element={<SampleDatePickerUsage />} />
      <Route path="sample-dropdown" element={<SampleDropdownUsage />} />

      {/* ==================== PRODUCT ADMIN ROUTES (Role 1) ==================== */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={[1]}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="profile" element={<Profile />} />
        <Route path="license" element={<LicenseList />} />
        <Route path="licenseCreate" element={<LicenseCreate />} />
        <Route path="masters/users" element={<UserManagement />} />
        <Route path="masters/school" element={<School />} />
        <Route path="masters/request-demo" element={<RequestDemo />} />
      </Route>

      {/* ==================== USER LAYOUT ROUTES (Roles 2-6) ==================== */}
      <Route
        path="/user"
        element={
          <ProtectedRoute allowedRoles={[2, 3, 4, 5, 6]}>
            <UserLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        {renderUserRoutes(user)}
      </Route>

      {/* ==================== SMART CATCH-ALL ==================== */}
      <Route path="*" element={<SmartCatchAll />} />
    </Routes>
  );
};

export default AppRoutes;
